import React from "react";
import { useNavigate } from "react-router-dom";
import PageBanner from "../Common/PageBanner";
import ScrollReveal from "../Common/ScrollReveal";
import { useGetBlogsQuery } from "../../store/blogsApi";

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const Blogs = () => {
  const navigate = useNavigate();
  const { data: blogs = [], isLoading, isError, error } = useGetBlogsQuery();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f9fa] to-white font-[Segoe_UI,Arial,sans-serif]">
      <ScrollReveal variant="fadeUp" amount={0.2}>
      <PageBanner
        title="Blogs & Insights"
        subtitle="Stay informed with the latest updates from Pawansut Holdings"
      />

      {/* Main Section */}
      <section
        className="relative overflow-hidden py-8 px-4 md:py-16 md:px-4"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none z-[1]"
          style={{
            background: `radial-gradient(circle at 20% 30%, rgba(0,0,0,0.03) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(0,0,0,0.03) 0%, transparent 50%)`,
          }}
        />
        <div className="max-w-[1200px] mx-auto relative z-[2]">
          <h2 className="text-xl sm:text-2xl md:text-[2.1rem] font-black text-black mb-6 md:mb-10 tracking-wide uppercase relative text-center after:content-[''] after:absolute after:bottom-[-10px] after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-gradient-to-r after:from-black after:to-[#333] after:rounded-sm">
            Latest Articles
          </h2>
          {isLoading && (
            <div className="text-center py-16 text-[#666] font-medium">
              Loading articles...
            </div>
          )}
          {isError && (
            <div className="text-center py-16 text-red-600 font-medium">
              {error?.data?.message || "Failed to load blogs. Please try again."}
            </div>
          )}
          {!isLoading && !isError && blogs.length === 0 && (
            <div className="text-center py-16 text-[#666] font-medium">
              No articles yet.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {blogs.map((blog) => (
              <article
                key={blog.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/blogs/${blog.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/blogs/${blog.id}`);
                  }
                }}
                className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-[#e8e8e8] hover:border-black transition-all duration-300 flex flex-col hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] cursor-pointer"
              >
              <div className="relative w-full h-[200px] md:h-[220px] overflow-hidden">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 50%)",
                  }}
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-base md:text-lg font-bold text-black mb-4 leading-snug tracking-wide line-clamp-2">
                  {blog.title}
                </h3>
                <div className="flex flex-wrap gap-4 mb-4 text-[#666] text-sm font-medium">
                  <span className="flex items-center gap-1.5 shrink-0">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-[#333] shrink-0"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    {blog.author}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-[#333] shrink-0"
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                      />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {formatDate(blog.uploadedAt)}
                  </span>
                </div>
                <p className="text-[#333] text-sm md:text-base leading-relaxed line-clamp-3">
                  {blog.description}
                </p>
              </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>
    </div>
  );
};

export default Blogs;
